import React, { memo, useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { Button } from '@saleshandy/design-system';
import Switch, { Size as SwitchSize } from '../../../../../../../../shared/design-system/components/atoms/switch';
import Icon from '../../../../../../../../shared/design-system/components/atoms/icon';
import type { IProps } from './types';
import { getIsRequestPending } from '../../../../../../../../shared/utils';
// import './_dialer-settings-modal.scss';

const DialerSettingsModal: React.FC<IProps> = ({
    show,
    onClose,
    onSubmit,
    dialerLocalPresenceEnabled,
    dialerNumberRotationEnabled,
    updateAdminSettingsRequestStatus,
}) => {
    const [localPresence, setLocalPresence] = useState<boolean>(false);
    const [numberRotation, setNumberRotation] = useState<boolean>(false);
    const isUpdateAdminSettingsRequestPending = getIsRequestPending(updateAdminSettingsRequestStatus);

    useEffect(() => {
        if (show) {
            setLocalPresence(dialerLocalPresenceEnabled ?? false);
            setNumberRotation(dialerNumberRotationEnabled ?? false);
        }
    }, [show, dialerLocalPresenceEnabled, dialerNumberRotationEnabled]);

    // Check if there are any changes from the original API values
    const hasChanges = useMemo(
        () => localPresence !== (dialerLocalPresenceEnabled ?? false)
            || numberRotation !== (dialerNumberRotationEnabled ?? false),
        [localPresence, numberRotation, dialerLocalPresenceEnabled, dialerNumberRotationEnabled],
    );

    const handleConfirm = () => {
        onSubmit?.(localPresence, numberRotation);
    };

    const handleCancel = () => {
        setLocalPresence(dialerLocalPresenceEnabled ?? false);
        setNumberRotation(dialerNumberRotationEnabled ?? false);
        onClose();
    };

    return (
        <Modal
            show={show}
            onHide={handleCancel}
            aria-labelledby="dialer-settings-modal-title"
            className="dialer-settings-modal"
            backdrop="static"
            centered
        >
            <Modal.Header className="dialer-settings-modal__header">
                <div className="dialer-settings-modal__header-content">
                    <Icon identifier="setting" className="dialer-settings-modal__header-icon" />
                    <Modal.Title id="dialer-settings-modal-title" className="dialer-settings-modal__title">
                        Dialer Settings
                    </Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body className="dialer-settings-modal__body">
                <div className="dialer-settings-modal__setting-item">
                    <div className="dialer-settings-modal__setting-header">
                        <Switch
                            checked={localPresence}
                            onChange={(checked) => setLocalPresence(checked)}
                            size={SwitchSize.Small}
                        />
                        <span className="dialer-settings-modal__setting-label">
                            Local Presence
                        </span>
                    </div>
                    <p className="dialer-settings-modal__setting-description">
                        Automatically pick the closest matching local number from your available phone numbers based on the prospect's area code.
                    </p>
                </div>
                <div className="dialer-settings-modal__setting-item">
                    <div className="dialer-settings-modal__setting-header">
                        <Switch
                            checked={numberRotation}
                            onChange={(checked) => setNumberRotation(checked)}
                            size={SwitchSize.Small}
                        />
                        <span className="dialer-settings-modal__setting-label">
                            Number Rotation
                        </span>
                    </div>
                    <p className="dialer-settings-modal__setting-description">
                        Automatically rotate between your available phone numbers to reduce spam risks and improve call pickup rate.
                    </p>
                </div>
            </Modal.Body>
            <Modal.Footer className="dialer-settings-modal__footer">
                <div className="dialer-settings-modal__footer-actions">
                    <Button
                        onClick={handleCancel}
                        variant="secondary"
                        className="dialer-settings-modal__cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleConfirm}
                        className="dialer-settings-modal__confirm-btn"
                        disabled={isUpdateAdminSettingsRequestPending || !hasChanges}
                        isLoading={isUpdateAdminSettingsRequestPending}
                        loadingText="Updating..."
                    >
                        Confirm
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default memo(DialerSettingsModal);

